import { readonly, ref, type Ref } from 'vue';
import { toSettingsNameConflict, type SettingsNameConflict } from '../lib/settings-name-conflict';
import { toSettingsValidationMessages } from '../lib/settings-validation-error';
import type { SettingsListQuery } from '../lib/settings-list-query';

export interface SettingsReadState<T> {
  items: Ref<T[]>;
  isLoading: Ref<boolean>;
  readError: Ref<string | null>;
  query: Ref<SettingsListQuery>;
  fetchAll(next?: SettingsListQuery): Promise<boolean>;
}

/**
 * The reading side of a settings listing: the query in force, the rows it answered with and the
 * failure of that read, kept apart from anything a write reports.
 */
export function createSettingsReadState<T>(
  list: (query: SettingsListQuery) => Promise<T[]>,
  fallbackMessage: string
): SettingsReadState<T> {
  const items = ref<T[]>([]) as Ref<T[]>;
  const isLoading = ref(false);
  const readError = ref<string | null>(null);
  const query = ref<SettingsListQuery>({});
  let latestRead = 0;

  async function fetchAll(next?: SettingsListQuery): Promise<boolean> {
    if (next) query.value = next;

    const read = (latestRead += 1);
    const requestedQuery = query.value;
    isLoading.value = true;

    try {
      const listed = await list(requestedQuery);
      // Only the newest read may publish: the rows of an older answer, its emptiness and its
      // failure all belong to a query the screen has already replaced. A superseded read answers
      // false so its caller asks again instead of recording its query as the one on screen.
      if (read !== latestRead) return false;

      items.value = listed;
      readError.value = null;
      return true;
    } catch (error: unknown) {
      if (read !== latestRead) return false;

      readError.value = error instanceof Error ? error.message : fallbackMessage;
      return false;
    } finally {
      if (read === latestRead) isLoading.value = false;
    }
  }

  return { items, isLoading, readError, query, fetchAll };
}

export interface SettingsMutationState {
  mutationError: Ref<string | null>;
  nameConflict: Ref<SettingsNameConflict | null>;
  validationMessages: Ref<readonly string[]>;
  reset(): void;
  record(error: unknown, fallbackMessage: string): void;
}

/**
 * The writing side: what the last create, update or delete was refused with. It never reaches the
 * listing, so a refused write cannot turn rows that were read into a load failure.
 */
export function createSettingsMutationState(): SettingsMutationState {
  const mutationError = ref<string | null>(null);
  const nameConflict = ref<SettingsNameConflict | null>(null);
  const validationMessages = ref<readonly string[]>([]);

  function reset(): void {
    mutationError.value = null;
    nameConflict.value = null;
    validationMessages.value = [];
  }

  function record(error: unknown, fallbackMessage: string): void {
    nameConflict.value = toSettingsNameConflict(error);
    validationMessages.value = toSettingsValidationMessages(error);
    mutationError.value = error instanceof Error ? error.message : fallbackMessage;
  }

  return { mutationError, nameConflict, validationMessages, reset, record };
}

export interface SettingsMutationFlags {
  isCreating: Ref<boolean>;
  isUpdating: Ref<boolean>;
  isDeleting: Ref<boolean>;
}

export function exposeSettingsListState<T>(
  read: SettingsReadState<T>,
  mutation: SettingsMutationState,
  flags: SettingsMutationFlags
) {
  return {
    items: readonly(read.items),
    isLoading: readonly(read.isLoading),
    isCreating: readonly(flags.isCreating),
    isUpdating: readonly(flags.isUpdating),
    isDeleting: readonly(flags.isDeleting),
    readError: readonly(read.readError),
    mutationError: readonly(mutation.mutationError),
    nameConflict: readonly(mutation.nameConflict),
    validationMessages: readonly(mutation.validationMessages),
    query: readonly(read.query),
    fetchAll: read.fetchAll,
  };
}
